import { _getInstance } from "@firebase/auth/internal";
import { FirebaseError } from "firebase/app";
import {
  browserPopupRedirectResolver,
  GoogleAuthProvider,
  linkWithPopup,
  OAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signOut,
  unlink,
  type Auth,
  type OAuthCredential,
  type User,
} from "firebase/auth";
import { config } from "../config/env";
import { getFirebaseAuth } from "../lib/firebase";
import { PAYMENTS_ENABLED } from "../config/payments";
import { checkoutInFlight, guestRotationSettled, identityCreatedAt, isRetirementPending, retirementEpoch } from "../lib/guestSession";
import { isKnownInAppBrowser } from "./inAppBrowser";
import { holdProviderName, providerNameFrom } from "./providerName";

/**
 * Keep the name this sign-in result shared for the UID it produced. Never
 * throws: a name is a convenience on the create form, and must not be able to
 * fail a sign-in that already succeeded.
 */
function keepProviderName(result: unknown, id: BookProvider, fallbackSource?: unknown): void {
  const uid = (result as { user?: { uid?: unknown } } | null | undefined)?.user?.uid;
  if (typeof uid !== "string") return;
  holdProviderName(uid, (fallbackSource === undefined ? null : providerNameFrom(fallbackSource, id)) ?? providerNameFrom(result, id));
}
import { afterFirstPaint } from "../../../../shared/lib/afterFirstPaint";

export type BookProvider = "apple" | "google";

// ACTIVATION (feature/book-provider-signin-activation, not merged). On only
// with Book's own authDomain in each environment's build config; the host
// guard below still withholds the buttons anywhere authDomain is not this
// Book host. Deploying it still needs the operator checks in
// docs/architecture/book-provider-sign-in.md: Firebase authorized domain,
// both provider callbacks, reCAPTCHA host allowance and connected Dev proof.
export const BOOK_PROVIDER_SIGN_IN_ENABLED = true;

export function bookProviderHostReady(authDomain: string, webHost: string): boolean {
  return authDomain === webHost;
}

function currentUserAgent(): string {
  return typeof navigator === "undefined" ? "" : navigator.userAgent;
}

/**
 * Whether Apple and Google may be offered at all on this page.
 *
 * Known in-app browsers (Instagram, Facebook, TikTok, ...) are excluded:
 * Google refuses OAuth inside embedded webviews and their popups are
 * unreliable, so a button there is one that cannot finish. Email and password
 * stay available.
 */
export function providerSignInAvailable(input: {
  enabled: boolean;
  authDomain: string;
  webHost: string;
  userAgent: string;
}): boolean {
  return input.enabled &&
    bookProviderHostReady(input.authDomain, input.webHost) &&
    !isKnownInAppBrowser(input.userAgent);
}

export function canOfferBookProviderSignIn(): boolean {
  return providerSignInAvailable({
    enabled: BOOK_PROVIDER_SIGN_IN_ENABLED,
    authDomain: config.firebase.authDomain,
    webHost: config.webHost,
    userAgent: currentUserAgent(),
  });
}

function providerFor(id: BookProvider): GoogleAuthProvider | OAuthProvider {
  if (id === "google") return new GoogleAuthProvider();
  const apple = new OAuthProvider("apple.com");
  apple.addScope("email");
  apple.addScope("name");
  return apple;
}

const RESOLVER_TIMEOUT_MS = 15_000;
let resolverReady: Promise<void> | null = null;

// Do not install a popup resolver at initializeAuth: WebKit can then stall the
// initial auth state indefinitely when Google's iframe is black-holed. Warm
// only the Book sign-in screen, and bound just resolver loading, not the time a
// person spends in the Apple/Google popup.
async function ensureResolverReady(): Promise<void> {
  if (resolverReady) return resolverReady;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new Error("Provider resolver unavailable")), RESOLVER_TIMEOUT_MS);
  });
  resolverReady = Promise.race([
    (async () => {
      const resolver = _getInstance<{ _initialize(auth: Auth): Promise<unknown> }>(
        browserPopupRedirectResolver
      );
      await resolver._initialize(getFirebaseAuth());
    })(),
    timeout,
  ]).then(() => undefined).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  }).catch((error: unknown) => {
    resolverReady = null;
    throw error;
  });
  return resolverReady;
}

/**
 * Warms the popup resolver for the sign-in screen and reports whether it is
 * ready (`true`) or failed (`false`). Returns a cancel for the effect cleanup:
 * a warm not yet started never starts, and a settled one reports nothing.
 *
 * NEVER BEFORE FIRST PAINT. The warm injects apis.google.com/js/api.js; started
 * while the document is still loading, a content blocker that drops it keeps
 * `load` from ever firing, and WebKit holds a short page's first paint until
 * load. App's /login went blank that way (c2 on Dev, b6a58ba). This screen
 * ran the same pattern (requested before `load`, which then never fired) and
 * painted in the probes only because WebKit judged it non-empty; it is
 * scheduled the same way rather than left to that heuristic. See
 * shared/lib/afterFirstPaint.ts. A tap still goes through
 * `ensureResolverReady`, bounded, in `signInWithBookProvider`.
 */
export function warmBookProviderSignIn(onSettled: (ready: boolean) => void): () => void {
  let live = true;
  const cancelScheduled = afterFirstPaint(() => {
    if (!canOfferBookProviderSignIn()) {
      onSettled(true);
      return;
    }
    ensureResolverReady().then(
      () => { if (live) onSettled(true); },
      () => { if (live) onSettled(false); }
    );
  });
  return () => {
    live = false;
    cancelScheduled();
  };
}

function errorCode(error: unknown): unknown {
  return typeof error === "object" && error !== null && "code" in error ? error.code : null;
}

function credentialFromError(id: BookProvider, error: unknown): OAuthCredential | null {
  if (!(error instanceof FirebaseError)) return null;
  return id === "google"
    ? GoogleAuthProvider.credentialFromError(error)
    : OAuthProvider.credentialFromError(error);
}

/**
 * How the provider attempt changed the Book identity.
 *
 * - `linked`: the anonymous guest gained the provider credential and KEPT its
 *   UID, so a hold the guest owns is still theirs (Q16: the anonymous UID is
 *   the booking's clientId; conversion links, it never replaces).
 * - `switched`: the provider account already belongs to another Firebase
 *   user. The guest's hold was released under the guest's UID first, then Book
 *   signed in as that existing user.
 * - `signed-in`: there was no guest to keep (none, or one whose retirement is
 *   pending); a plain popup sign-in.
 */
export type ProviderSignInOutcome = "linked" | "switched" | "signed-in";

/**
 * Sign in with Apple or Google on book.*.
 *
 * `beforeIdentitySwitch` runs, and is awaited, BEFORE any call that changes
 * the current UID, and never on a link, which keeps it. It is how the caller
 * releases a hold with the identity that owns it: a hold is authorized by the
 * uid on the token, so a release sent after the switch would be refused and
 * the slot left blocked until expiry, and a commit under the new UID must
 * never reuse it.
 *
 * On the no-guest path the callback runs before the popup opens. With no
 * anonymous user there is no hold to release, so `useHold().release` settles
 * in microtasks and the popup keeps its user activation.
 */
export async function signInWithBookProvider(
  id: BookProvider,
  beforeIdentitySwitch: () => Promise<void>
): Promise<ProviderSignInOutcome> {
  if (!canOfferBookProviderSignIn()) {
    throw new Error("Book provider sign-in is not activated for this host.");
  }
  await ensureResolverReady();
  return linkOrSignInWithProvider(getFirebaseAuth(), id, beforeIdentitySwitch);
}

/** The identity decision, separated from activation so it is testable while
 *  every build keeps the flag off. Callers go through signInWithBookProvider. */
export async function linkOrSignInWithProvider(
  auth: Auth,
  id: BookProvider,
  beforeIdentitySwitch: () => Promise<void>
): Promise<ProviderSignInOutcome> {
  // A rotation under way decides which identity is current, so read it after.
  // With none under way this settles in a microtask, as `ensureResolverReady`
  // already does, and the popup keeps the click's user activation. Only while
  // a sign-out is actually in flight can the wait cost it: the browser then
  // blocks the popup and the person is told to try again ("blocked"), which
  // is better than linking an account to an identity being retired.
  await guestRotationSettled();
  const provider = providerFor(id);
  const guest = auth.currentUser;

  // NEVER an identity whose retirement is pending (security audit F1): it made
  // a booking on this device, possibly someone else's, and linking would hand
  // that booking to this account. It is replaced by a plain sign-in instead.
  // The same while a hosted checkout is in flight for it (checkout design H1):
  // on a shared device the next person must not link an account to it.
  if (guest?.isAnonymous && (isRetirementPending() || (PAYMENTS_ENABLED && checkoutInFlight(undefined, Date.now(), identityCreatedAt(guest))))) {
    // Its hold is released under its own token before the switch. STARTED
    // before the popup, which must open from the click, and awaited after: the
    // release is on the wire long before a person finishes the provider's page.
    const releasing = beforeIdentitySwitch();
    try {
      keepProviderName(await signInWithPopup(auth, provider, browserPopupRedirectResolver), id);
    } finally {
      await releasing;
    }
    return "signed-in";
  }

  if (guest?.isAnonymous) {
    // Read BEFORE the popup: the person may leave it open for minutes, and
    // nothing about the identity is settled until it closes (W1).
    const epoch = retirementEpoch();
    let linked: Awaited<ReturnType<typeof linkWithPopup>>;
    try {
      linked = await linkWithPopup(guest, provider, browserPopupRedirectResolver);
    } catch (error) {
      // Every other refusal leaves the guest exactly as it was.
      if (errorCode(error) !== "auth/credential-already-in-use") throw error;
      // This Apple/Google account is already a Lura user. The popup has done
      // its work, so no second window is needed: sign in with the credential
      // it returned. Never merge the two UIDs and never match on email.
      const credential = credentialFromError(id, error);
      if (!credential) throw error;
      // The name Apple shares once came back on the REFUSED link, not on this
      // sign-in (the credential carries no name), so it is read from the error.
      await beforeIdentitySwitch();
      keepProviderName(await signInWithCredential(auth, credential), id, error);
      return "switched";
    }
    /*
     * RE-CHECKED AFTER THE POPUP (security re-audit W1). Firebase links the
     * User object it was handed, whatever is current by then: a link that
     * completes after this identity was rotated out, or after a retirement
     * was recorded for it (it made a booking, possibly someone else's), has
     * attached this provider account to a UID that is no longer this
     * person's. Undo it, and never report "linked".
     */
    // A checkout that started in another tab while the popup was open counts
    // too (H1; security code review F-5).
    if (auth.currentUser !== guest || retirementEpoch() !== epoch || (PAYMENTS_ENABLED && checkoutInFlight(undefined, Date.now(), identityCreatedAt(guest)))) {
      // Always throws, so no name is held for an undone link.
      await undoStaleLink(auth, guest, provider.providerId);
    }
    keepProviderName(linked, id);
    return "linked";
  }

  await beforeIdentitySwitch();
  keepProviderName(await signInWithPopup(auth, provider, browserPopupRedirectResolver), id);
  return "signed-in";
}

/**
 * The identity changed while the provider popup was open, so the link was
 * undone (security re-audit W1). Retrying is safe: the next attempt finds no
 * guest, or a guest whose retirement is pending, and signs in without
 * linking. Classified as "provider" ("Sign-in didn't finish. Try again.").
 */
export class ProviderIdentityChangedError extends Error {
  readonly code = "lura/identity-changed";
  constructor() {
    super("The identity changed while the provider sign-in was open; try again.");
    this.name = "ProviderIdentityChangedError";
  }
}

/**
 * Takes the provider back off a UID it should never have reached, then
 * leaves that UID: signed out if it is still current, because it now owns a
 * booking (or is a retired identity) and is no longer anonymous, so the
 * ordinary rotation would refuse it. Always throws. If the unlink itself
 * fails the link stays server-side, so it is still never reported as linked
 * and the device still leaves that identity.
 */
async function undoStaleLink(auth: Auth, guest: User, providerId: string): Promise<never> {
  try {
    await unlink(guest, providerId);
  } catch {
    // Reported below like every other outcome here: identity changed.
  }
  if (auth.currentUser?.uid === guest.uid) {
    try {
      await signOut(auth);
    } catch {
      // Nothing more this page can do; the caller is told to try again.
    }
  }
  throw new ProviderIdentityChangedError();
}

/**
 * - cancelled: deliberate abandonment, silent.
 * - collision: the email belongs to an account that uses another method.
 * - blocked: the browser refused the window.
 * - unconfigured: this host or provider is not set up in Firebase. Retrying
 *   here cannot succeed, so the caller withdraws the buttons.
 * - provider: anything else.
 */
export type ProviderFailure = "cancelled" | "collision" | "blocked" | "unconfigured" | "provider";

export function classifyProviderFailure(error: unknown): ProviderFailure {
  const code = errorCode(error);
  if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request" ||
      code === "auth/user-cancelled") return "cancelled";
  if (code === "auth/account-exists-with-different-credential" ||
      code === "auth/email-already-in-use") return "collision";
  if (code === "auth/popup-blocked" || code === "auth/operation-not-supported-in-this-environment") {
    return "blocked";
  }
  if (code === "auth/unauthorized-domain" || code === "auth/operation-not-allowed") {
    return "unconfigured";
  }
  return "provider";
}
